import { useForm, useFieldArray } from "react-hook-form";
import type { UseFormReturn } from "react-hook-form";
import DataTable from "../common/table";
import { Button, FormField, Form } from "../ui";
import type { ColumnDef } from "../ui";
import { formFromStructure } from "./so-form";
import { useTranslation } from "react-i18next";
import { FormFieldType, getField } from "./fields";
import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ComboBox } from "../common/combo-box";
import { exportFormCsv, importFormCsv } from "~/utils/csv-util";
import type { Table } from "@tanstack/react-table";
import { defaultTableColumns } from "~/utils/table-util";
import type { FormOption, FormStructure } from "~/utils/form-util";

export type BulkFormData<T = any> = { items: T[] };

export function BulkEditableTable<T>({
  structure,
  data,
  onSubmit,
}: {
  structure: FormStructure<T>;
  data: T[];
  onSubmit: (rows: BulkFormData<T>) => void;
}) {
  const [table, setTable] = useState<Table<any>>();
  const form = useForm<BulkFormData<any>>({ defaultValues: { items: data } });
  const { fields, append, replace } = useFieldArray<BulkFormData<any>>({
    control: form.control,
    name: "items",
  });
  const { t } = useTranslation();

  const [columns, setColumns] = useState<ColumnDef<any>[]>([]);
  useEffect(() => {
    setColumns(fromFormStructureToColDefs(structure, t, form));
  }, [structure, data, append]);

  function handleExport() {
    exportFormCsv(form.getValues().items, structure);
  }

  function handleImport(file: File) {
    importFormCsv(file, structure, (rows) => {
      replace(rows);
    });
  }

  function handleDelete() {
    if (!table) {
      console.warn("Table is not ready yet");
      return;
    }

    const selectedRows = table?.getSelectedRowModel().flatRows || [];
    const selectedIndices = selectedRows.map((r) => r.index);
    const newItems = form
      .getValues()
      .items.filter((_, index) => !selectedIndices.includes(index));
    form.reset({ items: newItems });
    table?.resetRowSelection(true);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <DataTable
          data={fields}
          columns={columns}
          searchbar
          selectable
          tableReady={setTable}
          actions={
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => append(formFromStructure(structure) as T)}
              >
                {t("common.addRow")}
              </Button>

              <input
                type="file"
                accept=".csv"
                className="hidden"
                id="csv-upload"
                onChange={(e) => {
                  if (e.target.files?.[0]) handleImport(e.target.files[0]);
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="m-2"
                onClick={() => document.getElementById("csv-upload")?.click()}
              >
                {t("common.import")}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="mr-2"
                onClick={handleExport}
              >
                {t("common.export")}
              </Button>
            </>
          }
          selectionActions={
            <Button type="button" variant="destructive" onClick={handleDelete}>
              {t("common.deleteSelected")}
            </Button>
          }
        />

        <Button type="submit">{t("common.save")}</Button>
      </form>
    </Form>
  );
}

export function fromFormStructureToColDefs<T>(
  structure: FormStructure<T>,
  t: (key: string) => string,
  form: UseFormReturn<{ items: T[] }, any, { items: T[] }>,
): ColumnDef<T>[] {
  const colDefs: ColumnDef<T>[] = [];
  colDefs.push(defaultTableColumns["select"] as ColumnDef<T>);

  for (const [key, def] of Object.entries(structure) as [keyof T, any][]) {
    if (key === "virtualFields") continue;
    if (def.hidden) continue;

    colDefs.push({
      id: key as string,
      accessorKey: key as string,
      header: () => renderHeader(t(def.label), structure, key, form),
      cell: ({ row }) => renderBareField(t, structure, key, form, row.index),
    });
  }

  return colDefs;
}

export function renderBareField<T>(
  t: (key: string) => string,
  structure: FormStructure<T>,
  key: keyof T,
  form: UseFormReturn<{ items: T[] }, any, { items: T[] }>,
  rowIndex: number,
): ReactNode {
  return (
    <FormField
      control={form.control}
      name={`items.${rowIndex}.${key as string}` as any}
      render={({ field }) =>
        getField(t, structure, key, field, form) as ReactElement
      }
    />
  );
}

export function renderHeader<T>(
  label: string,
  structure: FormStructure<T>,
  key: keyof T,
  form: UseFormReturn<{ items: T[] }, any, { items: T[] }>,
): ReactNode {
  const s = structure[key];
  if (s.type === FormFieldType.COMBOBOX) {
    // Create a combobox that allows to select one option and set it for each row
    function setAll(value: any) {
      const items = (form.getValues("items") as any[]) || [];
      const newItems = items.map((item) => ({
        ...item,
        [key]: value,
      }));
      form.reset({ items: newItems });
    }

    return (
      <ComboBox
        options={s.options as FormOption<any>[]}
        onChange={setAll}
        placeholder={label}
      />
    );
  }

  return <span>{label}</span>;
}
